import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../App.css';
import Papa from 'papaparse';

function KitschOrderDetailUpdate() {
    // Default API credentials - will be overridden by settings
    const [apiSettings, setApiSettings] = useState({
        apiKey: '',
        apiSecret: '',
        tag: ''
    });

    const [authHeader, setAuthHeader] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [results, setResults] = useState([]);
    const [fileName, setFileName] = useState('');
    const [globalSettings, setGlobalSettings] = useState({
        carrierCode: 'usps',
        shipDate: new Date().toISOString().split('T')[0],
        notifyCustomer: true,
        notifySalesChannel: true
    });

    const carrierCodes = [
        { value: 'usps', label: 'USPS' },
        { value: 'fedex', label: 'FedEx' },
        { value: 'ups', label: 'UPS' },
        { value: 'dhl', label: 'DHL' },
        { value: 'other', label: 'Other' }
    ];

    // Initialize auth header from stored settings
    useEffect(() => {
        const storedSettings = localStorage.getItem('shipstationSettings');
        if (storedSettings) {
            const savedSettings = JSON.parse(storedSettings);
            setApiSettings(savedSettings);
            updateAuthHeader(savedSettings.apiKey, savedSettings.apiSecret);
        } else {
            updateAuthHeader(apiSettings.apiKey, apiSettings.apiSecret);
        }
    }, []);

    const updateAuthHeader = (key, secret) => {
        const header = `Basic ${btoa(key + ":" + secret)}`;
        setAuthHeader(header);
    };

    const handleSettingsSave = () => {
        if (!apiSettings.apiKey || !apiSettings.apiSecret) {
            alert('Please enter both API Key and API Secret');
            return;
        }

        // Update auth header with new credentials
        updateAuthHeader(apiSettings.apiKey, apiSettings.apiSecret);

        // Save to localStorage
        localStorage.setItem('shipstationSettings', JSON.stringify(apiSettings));
        setShowSettings(false);
        alert('Settings saved successfully!');
    };

    const handleSettingsClear = () => {
        const defaultSettings = {
            apiKey: '',
            apiSecret: '',
            tag: ''
        };
        setApiSettings(defaultSettings);
        updateAuthHeader(defaultSettings.apiKey, defaultSettings.apiSecret);
        localStorage.removeItem('shipstationSettings');
        alert('Settings cleared to defaults!');
    };

    const handleApiSettingsChange = (e) => {
        const { name, value } = e.target;
        setApiSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                orderId: '12345678',
                trackingNumber: 'TRACK123456789',
                carrierCode: 'usps',
                shipDate: new Date().toISOString().split('T')[0],
                notifyCustomer: 'true',
                notifySalesChannel: 'true'
            },
            {
                orderId: '87654321',
                trackingNumber: 'TRACK987654321',
                carrierCode: 'fedex',
                shipDate: new Date().toISOString().split('T')[0],
                notifyCustomer: 'false',
                notifySalesChannel: 'true'
            }
        ];

        const csv = Papa.unparse(templateData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'shipstation_tracking_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setFileName(file.name);
        Papa.parse(file, {
            header: true,
            complete: (results) => {
                const validData = results.data.filter(row =>
                    row.orderId && row.trackingNumber &&
                    row.orderId.trim() !== '' && row.trackingNumber.trim() !== ''
                );
                setCsvData(validData);
                setResults([]);
                setProgress({ current: 0, total: validData.length, percentage: 0 });
            },
            error: (error) => {
                console.error('Error parsing CSV:', error);
                alert('Error parsing CSV file. Please check the format.');
            }
        });
    };

    const handleGlobalSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setGlobalSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const updateSingleOrder = async (orderData) => {
        try {
            const payload = {
                orderId: parseInt(orderData.orderId),
                carrierCode: orderData.carrierCode || globalSettings.carrierCode,
                shipDate: orderData.shipDate || globalSettings.shipDate,
                trackingNumber: orderData.trackingNumber,
                notifyCustomer: orderData.notifyCustomer !== undefined ?
                    (orderData.notifyCustomer === 'true' || orderData.notifyCustomer === true) :
                    globalSettings.notifyCustomer,
                notifySalesChannel: orderData.notifySalesChannel !== undefined ?
                    (orderData.notifySalesChannel === 'true' || orderData.notifySalesChannel === true) :
                    globalSettings.notifySalesChannel
            };

            // Add tag to payload if specified
            if (apiSettings.tag) {
                payload.tag = apiSettings.tag;
            }

            await axios.post(
                'https://ssapi.shipstation.com/orders/markasshipped',
                payload,
                {
                    headers: {
                        'Authorization': authHeader,
                        'Content-Type': 'application/json'
                    },
                    timeout: 20000
                }
            );

            return {
                success: true,
                orderId: orderData.orderId,
                trackingNumber: orderData.trackingNumber,
                message: 'Successfully updated'
            };
        } catch (error) {
            console.error(`Error updating order ${orderData.orderId}:`, error);
            let errorMessage = 'Unknown error';

            if (error.response?.status === 401) {
                errorMessage = 'Authentication failed - Please check your API credentials in Settings';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            return {
                success: false,
                orderId: orderData.orderId,
                trackingNumber: orderData.trackingNumber,
                message: errorMessage
            };
        }
    };

    const processAllOrders = async () => {
        if (csvData.length === 0) {
            alert('Please upload a CSV file first.');
            return;
        }

        if (!authHeader) {
            alert('Please configure your API credentials in Settings first.');
            setShowSettings(true);
            return;
        }

        setLoading(true);
        setResults([]);
        const processResults = [];

        for (let i = 0; i < csvData.length; i++) {
            const orderData = csvData[i];
            const currentProgress = i + 1;
            const percentage = Math.round((currentProgress / csvData.length) * 100);
            setProgress({
                current: currentProgress,
                total: csvData.length,
                percentage: percentage
            });

            const result = await updateSingleOrder(orderData);
            processResults.push(result);
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setResults(processResults);
        setLoading(false);
    };

    const downloadResults = () => {
        const resultsCsv = Papa.unparse(results);
        const blob = new Blob([resultsCsv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tracking-update-results-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const resetAll = () => {
        setCsvData([]);
        setResults([]);
        setFileName('');
        setProgress({ current: 0, total: 0, percentage: 0 });
        const fileInput = document.getElementById('csvFile');
        if (fileInput) fileInput.value = '';
    };

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    return (
        <div className='kitschOrderUpdate'>
            {/* Settings Popup */}
            {showSettings && (
                <div className="settings-popup-overlay">
                    <div className="settings-popup">
                        <div className="popup-header">
                            <h2>API Settings</h2>
                            <button
                                className="close-button"
                                onClick={() => setShowSettings(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="popup-content">
                            <div className="form-group">
                                <label>API Key</label>
                                <input
                                    type="password"
                                    name="apiKey"
                                    value={apiSettings.apiKey}
                                    onChange={handleApiSettingsChange}
                                    className="form-control"
                                    placeholder="Enter your API Key"
                                />
                            </div>

                            <div className="form-group">
                                <label>API Secret</label>
                                <input
                                    type="password"
                                    name="apiSecret"
                                    value={apiSettings.apiSecret}
                                    onChange={handleApiSettingsChange}
                                    className="form-control"
                                    placeholder="Enter your API Secret"
                                />
                            </div>

                            <div className="form-group">
                                <label>Tag (Optional)</label>
                                <input
                                    type="text"
                                    name="tag"
                                    value={apiSettings.tag}
                                    onChange={handleApiSettingsChange}
                                    className="form-control"
                                    placeholder="Enter a tag for orders"
                                />
                                <small className="form-hint">
                                    This tag will be applied to all updated orders
                                </small>
                            </div>

                            <div className="current-settings">
                                <h4>Current Configuration</h4>
                                <div className="settings-info">
                                    <p><strong>API Key:</strong> {apiSettings.apiKey ? '••••' + apiSettings.apiKey.slice(-4) : 'Not set'}</p>
                                    <p><strong>API Secret:</strong> {apiSettings.apiSecret ? '••••' + apiSettings.apiSecret.slice(-4) : 'Not set'}</p>
                                    <p><strong>Tag:</strong> {apiSettings.tag || 'Not set'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="popup-actions">
                            <button
                                onClick={handleSettingsClear}
                                className="btn-clear"
                            >
                                Clear Settings
                            </button>
                            <div className="action-buttons">
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSettingsSave}
                                    className="btn-primary"
                                >
                                    Save Settings
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="header-section">
                <div className="header-content">
                    <div className="header-text">
                        <h1>Kitsch Bulk Order Tracking Update</h1>
                        <p>Update tracking numbers for multiple orders simultaneously</p>
                    </div>
                    <button
                        className="settings-button"
                        onClick={() => setShowSettings(true)}
                    >
                        ⚙️ Settings
                    </button>
                </div>
            </div>

            {/* Template Download */}
            <div className="template-section">
                <div className="section-header">
                    <h2>Get Started</h2>
                    <p>Download our template to ensure proper formatting</p>
                </div>
                <div className="template-card">
                    <div className="template-content">
                        <div className="template-icon">📋</div>
                        <div className="template-info">
                            <h3>CSV Template</h3>
                            <p>Download our pre-formatted CSV template with example data and proper column headers.</p>
                            <ul>
                                <li><strong>orderId</strong> - ShipStation Order ID</li>
                                <li><strong>trackingNumber</strong> - Tracking number</li>
                                <li><strong>carrierCode</strong> - Carrier code (optional)</li>
                                <li><strong>shipDate</strong> - Ship date (optional)</li>
                            </ul>
                        </div>
                    </div>
                    <button onClick={downloadTemplate} className="btn-primary">
                        Download Template
                    </button>
                </div>
            </div>

            {/* Global Settings */}
            <div className="settings-section">
                <div className="section-header">
                    <h2>Global Settings</h2>
                    <p>These settings will be applied to all orders unless specified in the CSV file.</p>
                </div>
                <div className="settings-grid">
                    <div className="form-group">
                        <label>Default Carrier</label>
                        <select
                            name="carrierCode"
                            value={globalSettings.carrierCode}
                            onChange={handleGlobalSettingsChange}
                            className="form-control"
                        >
                            {carrierCodes.map(carrier => (
                                <option key={carrier.value} value={carrier.value}>
                                    {carrier.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Default Ship Date</label>
                        <input
                            type="date"
                            name="shipDate"
                            value={globalSettings.shipDate}
                            onChange={handleGlobalSettingsChange}
                            className="form-control"
                        />
                    </div>

                    <div className="checkbox-group">
                        <div className="checkbox-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="notifyCustomer"
                                    checked={globalSettings.notifyCustomer}
                                    onChange={handleGlobalSettingsChange}
                                />
                                <span className="checkmark"></span>
                                Notify Customer
                            </label>
                        </div>
                        <div className="checkbox-item">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="notifySalesChannel"
                                    checked={globalSettings.notifySalesChannel}
                                    onChange={handleGlobalSettingsChange}
                                />
                                <span className="checkmark"></span>
                                Notify Sales Channel
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* File Upload */}
            <div className="upload-section">
                <div className="section-header">
                    <h2>Upload CSV File</h2>
                    <p>Select your completed CSV file with order data</p>
                </div>
                <div className="upload-card">
                    <div className="upload-area">
                        <div className="upload-icon">📁</div>
                        <div className="upload-content">
                            <input
                                type="file"
                                id="csvFile"
                                accept=".csv"
                                onChange={handleFileUpload}
                                className="file-input"
                            />
                            <label htmlFor="csvFile" className="file-label">
                                Choose File
                            </label>
                            <span className="file-name">
                                {fileName || 'No file chosen'}
                            </span>
                        </div>
                        <p className="upload-hint">
                            Make sure your file includes at least <strong>orderId</strong> and <strong>trackingNumber</strong> columns
                        </p>
                    </div>

                    {csvData.length > 0 && (
                        <div className="file-preview">
                            <div className="preview-header">
                                <h4>File Preview</h4>
                                <span className="preview-count">{csvData.length} orders found</span>
                            </div>
                            <div className="preview-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Tracking Number</th>
                                            <th>Carrier</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {csvData.slice(0, 3).map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.orderId}</td>
                                                <td>{row.trackingNumber}</td>
                                                <td>{row.carrierCode || globalSettings.carrierCode}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {csvData.length > 3 && (
                                    <div className="preview-more">
                                        + {csvData.length - 3} more orders
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            {csvData.length > 0 && (
                <div className="action-section">
                    <div className="action-buttons">
                        <button
                            onClick={processAllOrders}
                            disabled={loading}
                            className="btn-process"
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Processing...
                                </>
                            ) : (
                                `Process ${csvData.length} Orders`
                            )}
                        </button>
                        <button onClick={resetAll} className="btn-secondary">
                            Reset
                        </button>
                    </div>
                </div>
            )}

            {/* Progress Bar */}
            {loading && (
                <div className="progress-section">
                    <div className="progress-header">
                        <h3>Processing Orders</h3>
                        <span>{progress.current} of {progress.total} ({progress.percentage}%)</span>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${progress.percentage}%` }}
                        ></div>
                    </div>
                </div>
            )}

            {/* Results */}
            {results.length > 0 && (
                <div className="results-section">
                    <div className="section-header">
                        <h2>Processing Results</h2>
                        <div className="results-summary">
                            <div className="summary-item success">
                                <span className="count">{successCount}</span>
                                <span className="label">Successful</span>
                            </div>
                            <div className="summary-item failed">
                                <span className="count">{failureCount}</span>
                                <span className="label">Failed</span>
                            </div>
                            <div className="summary-item total">
                                <span className="count">{results.length}</span>
                                <span className="label">Total</span>
                            </div>
                        </div>
                    </div>

                    <div className="results-actions">
                        <button onClick={downloadResults} className="btn-primary">
                            Download Results
                        </button>
                        <button onClick={resetAll} className="btn-secondary">
                            Start Over
                        </button>
                    </div>

                    <div className="results-table-container">
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Tracking Number</th>
                                    <th>Status</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((result, index) => (
                                    <tr key={index} className={result.success ? 'success' : 'failed'}>
                                        <td className="order-id">{result.orderId}</td>
                                        <td className="tracking-number">{result.trackingNumber}</td>
                                        <td className="status">
                                            <span className={`status-badge ${result.success ? 'success' : 'failed'}`}>
                                                {result.success ? 'Success' : 'Failed'}
                                            </span>
                                        </td>
                                        <td className="message">{result.message}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default KitschOrderDetailUpdate;
